import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { IntlService } from 'ember-intl';
import { t } from 'ember-intl';
import type { Ota } from 'frontend-utils/models';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { AVAILABLE_MEAL_PLANS } from 'lumen/components/parity/selections';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  MEALTYPE_ICONS,
  MEALTYPE_TRANSLATION_KEYS,
  type MealTypeValues,
  RATE_PARAMS,
  type RateParam,
} from 'lumen/utils/constants/general-constants';
import { RATE_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { getRateParamOptions } from 'lumen/utils/rates-selections';

interface HotelPricingAssistantFiltersPricingSelectionsDynamicRateSignature {
  Args: {
    los: number;
    maxPersons: string | null;
    mealPlan: MealTypeValues | null;
    platform: Platform;
    rateCode: string | null;
    rateParam: RateParam | null;
    selectedOTA: Ota | null;
    updateMealPlan: (mealPlan: MealTypeValues | null) => void;
    updateRateCode: (rateCode: string | null) => void;
    updateRateParam: (rateParam: RateParam | null) => void;
  };
}

// Filter component for DRP (Dynamic Rate Pricing)
export default class HotelPricingAssistantFiltersPricingSelectionsDynamicRate extends Component<HotelPricingAssistantFiltersPricingSelectionsDynamicRateSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: IntlService;

  @service declare usageAnalytics: Services['usage-analytics'];

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetMealTypeOptions() {
    return AVAILABLE_MEAL_PLANS.map((mealPlan) => ({
      id: mealPlan,
      label: this.intl.t(MEALTYPE_TRANSLATION_KEYS[mealPlan]),
      icon: MEALTYPE_ICONS[mealPlan],
    }));
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetRateTypeOptions() {
    // We reuse the Rate Insight rate type filter logic
    return getRateParamOptions(
      this.args.selectedOTA,
      this.args.platform,
      this.intl,
      this.hotelAndMarketService,
    );
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateCodeOptions() {
    return this.hotelAndMarketService.hotel.primaRateCodes;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedCompsetMealtype() {
    return this.compsetMealTypeOptions.find(
      (option) => option['id'].toString() === this.args.mealPlan?.toString(),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedCompsetRateType() {
    let rateParam = this.args.rateParam;

    // PriMa BE and FE uses 'best_flex' as the rate param value (which is the correct one)
    // However, Rate Insight uses 'bestflex' as the rate param value (legacy)
    // Therefore, following workaround to match BE returned 'best_flex' with RI defined 'bestflex'
    if (this.args.rateParam === RATE_TYPES.BEST_FLEX) {
      rateParam = RATE_PARAMS.BEST_FLEX;
    }

    return this.compsetRateTypeOptions.find(
      (option: Option) => option['id'] === rateParam,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRateCode() {
    return this.rateCodeOptions.find(
      (option: Option) => option['id'] === this.args.rateCode,
    );
  }

  /**
   * Tracks usage of the filter bar.
   * https://lighthouseintelligence.atlassian.net/wiki/spaces/PAV/pages/5008130274/DRP+Table+View+Read-only#%F0%9F%93%88--Tracking
   */
  trackUserFilterUsage({
    filter,
    value,
  }: {
    filter: string;
    value: unknown;
  }): void {
    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      pmsRate: this.args.rateCode,
      rateParam: this.args.rateParam,
      platform: this.args.platform,
      mealPlan: this.args.mealPlan
        ? parseInt(this.args.mealPlan.toString(), 10)
        : null,
      filterUpdated: filter,
      [filter]: value,
    });
  }

  @action
  onCompsetMealtypeChange(option: Option & { id: MealTypeValues }): void {
    this.args.updateMealPlan(option['id']);
    this.trackUserFilterUsage({ filter: 'mealPlan', value: option['id'] });
  }

  @action
  onCompsetRateTypeChange(option: Option & { id: RateParam }): void {
    this.args.updateRateParam(option['id']);
    this.trackUserFilterUsage({ filter: 'rateParam', value: option['id'] });
  }

  @action
  onRateCodeChange(option: Option & { id: string }): void {
    this.args.updateRateCode(option['id']);
    this.trackUserFilterUsage({ filter: 'pmsRate', value: option['id'] });
  }

  <template>
    <div class="flex flex-row items-center text-nowrap">
      <span class="subdued-text mr-200">{{t
          "pricing_assistant.data_sync_status.my_rates"
        }}</span>
      <div class="no-filter-content flex items-center">
        <PrismSelectInput
          @onChange={{this.onRateCodeChange}}
          @options={{this.rateCodeOptions}}
          @value={{this.selectedRateCode}}
        />
      </div>

      <div class="vertical-filter-divider mr-200" />

      <span class="subdued-text mr-200">{{t
          "pricing_assistant.data_sync_status.competitor_rates"
        }}</span>
      <div class="no-filter-content flex items-center">
        <PrismSelectInput
          @onChange={{this.onCompsetRateTypeChange}}
          @options={{this.compsetRateTypeOptions}}
          @value={{this.selectedCompsetRateType}}
        />
      </div>
      <span class="subdued-text text-200--regular">-</span>
      <div class="no-filter-content flex items-center">
        {{t "rates.nights" count=@los}}
      </div>
      <span class="subdued-text text-200--regular">-</span>
      <div class="no-filter-content flex items-center">
        {{t "rates.persons_selection" count=@maxPersons}}
      </div>
      <span class="subdued-text text-200--regular">-</span>
      <div class="no-filter-content flex items-center">
        {{t "rates.roomtypes.all"}}
      </div>
      <span class="subdued-text text-200--regular">-</span>
      <div class="no-filter-content flex items-center">
        <PrismSelectInput
          @onChange={{this.onCompsetMealtypeChange}}
          @options={{this.compsetMealTypeOptions}}
          @value={{this.selectedCompsetMealtype}}
        />
      </div>
    </div>
  </template>
}
